import { FormSection, FormInput, FormSelect } from "../../../../Components/Form";
import { Button } from "../../../../Components/Buttons";
import { FilterIcon } from "lucide-react";
import { BloodGroups, PatientTypes, WardTypes } from "./PatientSelectsData";
import { useFetchAllDoctorsQuery } from "../../../../stores/api/users.api";
import { useMemo, useState } from "react";
import { useFetchUnitByDistrictQuery, useFetchUnitsAllQuery } from "../../../../stores/api/units.api";
import { useFetchTalukaByDistrictQuery } from "../../../../stores/api/taluka.api";
import { formatDateOnly, toOptions } from "../../../../Components/Utils";
import { useFetchAllDistrictsQuery } from "../../../../stores/api/district.api";
import DateRangePicker from '@wojtekmaj/react-daterange-picker';
const FilterForm = ({
    setFilter = null,
    setShowFilter = null,
    filtersData,
    loading = false,
    handleReset,
    handleSubmit,
}) => {
    const [district_id, setSelectedDistrictID] = useState(
        filtersData?.district_id ?? "",
    );
    const [value, setValue] = useState([new Date(), new Date()]);

    const { data: districtsRow = [] } = useFetchAllDistrictsQuery();
    const districts = useMemo(
        () => (districtsRow || []).map((r) => ({ label: r.name, value: r.id })),
        [districtsRow]
    );
    const { data: districtTalukas } = useFetchTalukaByDistrictQuery(district_id, {
        skip: !district_id,
        refetchOnMountOrArgChange: true,
    })
    const talukas = useMemo(() => toOptions(districtTalukas), [districtTalukas]);
    const { data: districtUnitRows } = useFetchUnitByDistrictQuery(district_id, {
        skip: !district_id,
        refetchOnMountOrArgChange: true,
    })
    const units = useMemo(() => toOptions(districtUnitRows), [districtUnitRows]);

    const setField = (name, value) => {
        if (name == "district_id") {
            setSelectedDistrictID(value)
        }
        setFilter((prev) => ({ ...prev, [name]: value }))
    };
    const handleRegDate = (val) => {
        if (!val || !val[0] || !val[1]) {
            setField("reg_date", "");
            return;
        }

        const startDate = formatDateOnly(val[0]);
        const endDate = formatDateOnly(val[1]);

        setField("reg_date", {
            start: startDate,
            end: endDate
        });
    };
    const { data: allPhysiciansRow = [] } =
        useFetchAllDoctorsQuery();
    const allPhysicians = useMemo(
        () =>
            (allPhysiciansRow || []).map((r) => ({
                label: r.name,
                value: r.id,
            })),
        [allPhysiciansRow],
    );


    return <>
        <form className="mt-2" onSubmit={handleSubmit}>
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 p-6">
                <div className="mb-6">
                    <h2 className="text-xl font-semibold text-slate-800 dark:text-white">
                        Patient Filters
                    </h2>
                    <p className="text-sm text-slate-500 mt-1">
                        Apply filters to narrow down patient records.
                    </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">

                    <FormSelect
                        label="District"
                        options={districts}
                        value={filtersData?.district_id}
                        onChange={(val) => {
                            setField("district_id", val ?? "")
                        }}
                        disabled={loading}
                        blurInputOnSelect
                    />

                    <FormSelect
                        label="Unit"
                        value={filtersData?.unit_id}
                        onChange={(val) => {
                            setField("unit_id", val ?? "")
                        }}
                        options={units}
                        disabled={loading}
                        blurInputOnSelect
                    />

                    <FormSelect
                        label="Taluka"
                        value={filtersData?.taluka_id}
                        options={talukas}
                        onChange={(val) => {
                            setField("taluka_id", val ?? "")
                        }}
                        disabled={loading}
                        blurInputOnSelect
                    />

                    <FormSelect
                        label="Physician"
                        disabled={loading}
                        options={allPhysicians}
                        value={filtersData?.physician_id}
                        onChange={(val) => {
                            setField("physician_id", val ?? "")
                        }}
                        blurInputOnSelect
                    />
                    <FormInput
                        label="Registration Date"
                        type="date"
                        isDateRange={true}
                        disabled={loading}
                        value={
                            filtersData?.reg_date?.start && filtersData?.reg_date?.end
                                ? [new Date(filtersData.reg_date.start), new Date(filtersData.reg_date.end)]
                                : [null, null]
                        }
                        onChange={handleRegDate}
                    />
                    <FormSelect
                        label="Patient Type"
                        value={filtersData?.patient_type}
                        onChange={(val) => {
                            setField("patient_type", val ?? "")
                        }}
                        options={PatientTypes}
                        disabled={loading}
                        blurInputOnSelect
                    />
                    <FormSelect
                        label="Ward Type"
                        value={filtersData?.ward_type}
                        options={WardTypes}
                        disabled={loading}
                        onChange={(val) => {
                            setField("ward_type", val ?? "")
                        }}
                        blurInputOnSelect
                    />

                    <FormSelect
                        label="Blood Group"
                        value={filtersData?.blood_group}
                        options={BloodGroups}
                        disabled={loading}
                        onChange={(val) => {
                            setField("blood_group", val ?? "")
                        }}
                        blurInputOnSelect
                    />

                </div>

                <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-slate-200 dark:border-slate-700">
                    <Button
                        type="button"
                        onClick={() => setShowFilter(false)}
                        className="
                            !bg-red-100 
                            !text-red-700 
                            hover:!bg-red-600 
                            hover:!text-white
                            dark:!bg-red-800 
                            dark:!text-white
                            dark:hover:!bg-red-700          
                        "
                    >
                        Close Filter
                    </Button>
                    <Button
                        type="button"
                        onClick={handleReset}
                        className="!bg-slate-100 !text-slate-700 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-200"
                    >
                        {loading && (
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25" />
                                <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" className="opacity-75" />
                            </svg>
                        )}
                        {loading ? "Reseting..." : "Reset Filters"}
                    </Button>

                    <Button
                        type="submit"
                        className="!bg-emerald-600 !text-white hover:!bg-emerald-700"
                    >
                        {loading && (
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25" />
                                <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" className="opacity-75" />
                            </svg>
                        )}
                        {loading ? "Filtering..." : "Apply Filter"}
                    </Button>
                </div>

            </div>
        </form>
    </>
}

export default FilterForm;